import { Component } from '@theme/component';

const SELECTORS = {
  group: '[data-protocol-group]',
  input: '[data-protocol-product-input]',
  summaryGroups: '[data-protocol-summary-groups]',
  summaryEmpty: '[data-protocol-summary-empty]',
  summaryCount: '[data-protocol-summary-count]',
  summaryTotal: '[data-protocol-summary-total]',
  addButton: '[data-protocol-add-button]',
  summaryError: '[data-protocol-summary-error]',
};

const TIMING_ORDER = ['morning', 'midday', 'evening', 'enhance'];

class ProtocolBuilderComponent extends Component {
  #selections = new Map();

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();
    this.addEventListener('change', this.#onChange);
    this.#addButton = this.querySelector(SELECTORS.addButton);
    this.#addButton?.addEventListener('click', this.#onAddProtocol);
    this.#renderSummary();
  }

  disconnectedCallback() {
    super.disconnectedCallback && super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.#addButton?.removeEventListener('click', this.#onAddProtocol);
  }

  #addButton = null;

  #timingLabel(timing) {
    const map = {
      morning: this.dataset.i18nMorning,
      midday: this.dataset.i18nMidday,
      evening: this.dataset.i18nEvening,
    };
    return map[timing] || (timing.charAt(0).toUpperCase() + timing.slice(1));
  }

  #onChange = (event) => {
    const input = event.target.closest ? event.target.closest(SELECTORS.input) : null;
    if (!input) return;

    const key = input.type === 'checkbox' ? input.id : input.name;

    if (input.type === 'checkbox' && !input.checked) {
      this.#selections.delete(key);
    } else {
      this.#selections.set(key, {
        variantId: input.value,
        title: input.dataset.productTitle || '',
        price: input.dataset.productPrice || '',
        image: input.dataset.productImage || '',
        timing: input.dataset.timing || 'enhance',
        groupName: input.dataset.groupName || '',
      });
    }

    this.#renderSummary();
  };

  #groupedSelections() {
    const groups = new Map();
    this.#selections.forEach((value) => {
      const timing = value.timing || 'enhance';
      if (!groups.has(timing)) groups.set(timing, []);
      groups.get(timing).push(value);
    });
    return TIMING_ORDER.filter((timing) => groups.has(timing)).map((timing) => [timing, groups.get(timing)]);
  }

  #missingRequiredGroups() {
    const groups = this.querySelectorAll(SELECTORS.group);
    const missing = [];
    groups.forEach((group) => {
      if (group.dataset.required !== 'true') return;
      const checked = group.querySelector(SELECTORS.input + ':checked');
      if (!checked) missing.push(group.dataset.groupName || 'a required item');
    });
    return missing;
  }

  #formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  }

  #renderSummary() {
    const groupsEl = this.querySelector(SELECTORS.summaryGroups);
    const emptyEl = this.querySelector(SELECTORS.summaryEmpty);
    const countEl = this.querySelector(SELECTORS.summaryCount);
    const totalEl = this.querySelector(SELECTORS.summaryTotal);

    const grouped = this.#groupedSelections();

    if (emptyEl) emptyEl.hidden = grouped.length > 0;

    if (groupsEl) {
      groupsEl.querySelectorAll('[data-protocol-summary-group]').forEach((node) => node.remove());

      grouped.forEach(([timing, items]) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'protocol-builder__summary-group';
        wrapper.setAttribute('data-protocol-summary-group', '');
        wrapper.style.gap = '0.5rem';

        const label = document.createElement('span');
        label.className = 'protocol-builder__summary-group-timing';
        label.textContent = this.#timingLabel(timing);
        wrapper.appendChild(label);

        items.forEach((item) => {
          const line = document.createElement('div');
          line.className = 'protocol-builder__summary-item';
          line.style.display = 'grid';
          line.style.gridTemplateColumns = 'auto minmax(0, 1fr) auto';
          line.style.alignItems = 'center';
          line.style.gap = '0.6rem';

          if (item.image) {
            const img = document.createElement('img');
            img.className = 'protocol-builder__summary-item-img';
            img.src = item.image;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 40;
            img.height = 40;
            img.style.width = '40px';
            img.style.height = '40px';
            img.style.objectFit = 'cover';
            img.style.borderRadius = '6px';
            line.appendChild(img);
          }

          const name = document.createElement('span');
          name.className = 'protocol-builder__summary-item-name';
          name.textContent = item.title;
          name.style.overflow = 'hidden';
          name.style.textOverflow = 'ellipsis';
          name.style.whiteSpace = 'nowrap';
          name.style.fontSize = '0.9rem';
          line.appendChild(name);

          const price = document.createElement('span');
          price.className = 'protocol-builder__summary-item-price';
          price.textContent = this.#formatMoney(item.price);
          price.style.whiteSpace = 'nowrap';
          price.style.opacity = '0.8';
          price.style.fontSize = '0.9rem';
          line.appendChild(price);

          wrapper.appendChild(line);
        });

        groupsEl.appendChild(wrapper);
      });
    }

    let total = 0;
    this.#selections.forEach((value) => {
      total += Number(value.price) || 0;
    });

    if (countEl) {
      const count = this.#selections.size;
      const unit = count === 1 ? (this.dataset.i18nItem || 'item') : (this.dataset.i18nItems || 'items');
      countEl.textContent = `${count} ${unit}`;
    }

    if (totalEl) totalEl.textContent = this.#formatMoney(total);

    if (this.#addButton) {
      this.#addButton.disabled = this.#selections.size === 0 || this.#missingRequiredGroups().length > 0;
    }
  }

  #onAddProtocol = async (event) => {
    event.preventDefault();
    const addButton = event.currentTarget;
    const errorEl = this.querySelector(SELECTORS.summaryError);

    const missing = this.#missingRequiredGroups();
    if (missing.length > 0) {
      if (errorEl) {
        errorEl.hidden = false;
        const prefix = this.dataset.i18nSelectError || 'Please select a product for:';
        errorEl.textContent = `${prefix} ${missing.join(', ')}`;
      }
      return;
    }

    if (errorEl) errorEl.hidden = true;

    const protocolName = this.dataset.protocolName || '';
    const protocolId = this.dataset.protocolId || '';

    const items = Array.from(this.#selections.values())
      .map((value) => {
        const item = { id: Number(value.variantId), quantity: 1 };
        if (protocolName || protocolId) {
          const props = {};
          if (protocolName) props['Protocol'] = protocolName;
          if (protocolId) props['_protocol'] = protocolId;
          item.properties = props;
        }
        return item;
      })
      .filter((item) => item.id);

    if (items.length === 0) return;

    const root = window.Shopify?.routes?.root || '/';
    addButton.disabled = true;
    addButton.classList.add('btn--loading');

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Unable to add protocol to cart.');
      }

      window.location.href = `${root}cart`;
      return;
    } catch (error) {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = error instanceof Error ? error.message : (this.dataset.i18nGenericError || 'Something went wrong. Please try again.');
      }
      addButton.disabled = false;
      addButton.classList.remove('btn--loading');
    }
  };
}

if (!customElements.get('protocol-builder')) {
  customElements.define('protocol-builder', ProtocolBuilderComponent);
}